import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'solegaze_theme_mode';
const APPBAR_COLOR_KEY = 'solegaze_appbar_color';

const PRESET_COLORS = [
  { label: 'Navy', value: '#2c3e6e' },
  { label: 'Midnight', value: '#0f172a' },
  { label: 'Indigo', value: '#312e81' },
  { label: 'Slate', value: '#1e293b' },
  { label: 'Emerald', value: '#064e3b' },
  { label: 'Crimson', value: '#7f1d1d' },
  { label: 'Purple', value: '#4c1d95' },
  { label: 'Charcoal', value: '#18181b' },
  { label: 'Ocean', value: '#0c4a6e' },
  { label: 'Forest', value: '#14532d' },
];

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem(THEME_KEY) || 'dark';
  });
  const [appBarColor, setAppBarColor] = useState(() => {
    return localStorage.getItem(APPBAR_COLOR_KEY) || '#2c3e6e';
  });

  // Resolve actual theme (system → detect OS preference)
  const getResolvedMode = useCallback(() => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return mode;
  }, [mode]);

  const [resolvedMode, setResolvedMode] = useState(getResolvedMode);

  useEffect(() => {
    setResolvedMode(getResolvedMode());
  }, [mode, getResolvedMode]);

  // Listen to system preference changes when mode is 'system'
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setResolvedMode(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  // Persist
  useEffect(() => {
    localStorage.setItem(THEME_KEY, mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem(APPBAR_COLOR_KEY, appBarColor);
  }, [appBarColor]);

  // Apply CSS class on body
  useEffect(() => {
    document.body.setAttribute('data-theme', resolvedMode);
  }, [resolvedMode]);

  return (
    <ThemeContext.Provider value={{
      mode,
      setMode,
      resolvedMode,
      appBarColor,
      setAppBarColor,
      presetColors: PRESET_COLORS,
      isDark: resolvedMode === 'dark',
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export default ThemeContext;
